import type { MaybeRefOrGetter } from 'vue'

/**
 * The guest checkout of one interview prep (PRICES.interviewPrep), on the
 * token its preview was parked under (server/utils/interviewPrepOffer.ts).
 *
 * Like the other guest purchases, the LemonSqueezy checkout opens in an
 * iframe on the page and is watched while it is open: its own redirect lands
 * inside the iframe, so the page has to notice the payment itself. Shared by
 * the offer card (components/InterviewPrep/Offer.vue) and the prep's own page
 * (pages/interview-prep/[token].vue).
 */
const POLL_INTERVAL_MS = 3000
/** ~5 minutes: long enough for a slow card, short enough to stop by itself. */
const MAX_POLLS = 100

export function useInterviewPrepCheckout(token: MaybeRefOrGetter<string | null | undefined>, options: { onPaid?: (token: string) => void | Promise<void> } = {}) {
  return useGuestTokenCheckout('interview-prep', token, {
    // Where LemonSqueezy's own redirect lands: the prep, waiting for the webhook.
    redirectPath: current => `/interview-prep/${current}?paid=1`,
    onPaid: options.onPaid,
  })
}

/**
 * The same in-page checkout for any one-time product sold on a parked token:
 * also the tailored CV a free account previews in the editor
 * (components/CV/Editor/TailorPanel.vue), which redirects to its download,
 * the Civil Service application (pages/civil-service/[token].vue) and the
 * NHS supporting statement (pages/nhs-statement/[token].vue).
 */
export function useGuestTokenCheckout(
  planId: 'interview-prep' | 'tailored-pdf' | 'civil-service' | 'nhs-statement',
  token: MaybeRefOrGetter<string | null | undefined>,
  options: {
    redirectPath: (token: string) => string
    onPaid?: (token: string) => void | Promise<void>
    /** A discount code to open the checkout with (the Civil Service reminders' 50 %); a refused one opens it at full price. */
    discountCode?: () => string | null | undefined
  },
) {
  const { getPlan } = usePremiumPlans()
  const { createCheckoutSession, isScriptLoading } = useLemonSqueezy()
  const { trackPurchase } = useAdsConversion()
  const journey = useJourney()

  const checkoutUrl = ref<string | null>(null)
  const isStarting = ref(false)
  const error = ref(false)
  const paid = ref(false)
  /** On sale on its own (its LemonSqueezy variant is set). */
  const canBuy = computed(() => !!getPlan(planId) && !!toValue(token))

  let poll: ReturnType<typeof setInterval> | null = null
  function stopPoll() {
    if (poll) {
      clearInterval(poll)
      poll = null
    }
  }

  function startPoll() {
    stopPoll()
    let attempts = 0
    poll = setInterval(async () => {
      attempts++
      if (attempts > MAX_POLLS) {
        stopPoll()
        return
      }
      await checkPaid()
    }, POLL_INTERVAL_MS)
  }

  async function checkPaid(): Promise<boolean> {
    const current = toValue(token)
    if (!current || paid.value) return paid.value
    try {
      const { status } = await $fetch<{ status: string }>(`/api/guest-export/${current}`)
      if (status !== 'paid') return false
    } catch {
      return false
    }
    stopPoll()
    paid.value = true
    checkoutUrl.value = null
    trackPurchase(planId, current)
    journey.track('cv_purchase', { plan: planId, guest: true })
    await options.onPaid?.(current)
    return true
  }

  async function buy() {
    const plan = getPlan(planId)
    const current = toValue(token)
    if (!plan || !current || isStarting.value) return
    isStarting.value = true
    error.value = false
    try {
      const url = await createCheckoutSession({
        variantId: plan.variantId,
        planId: plan.id,
        guestToken: current,
        redirectUrl: `${window.location.origin}${options.redirectPath(current)}`,
        discountCode: options.discountCode?.() ?? undefined,
      })
      if (!url) {
        error.value = true
        return
      }
      checkoutUrl.value = url
      journey.track('cv_checkout_open', { plan: planId, guest: true })
      startPoll()
    } catch (err) {
      console.error(`Could not open the ${planId} checkout:`, err)
      error.value = true
    } finally {
      isStarting.value = false
    }
  }

  // Closing the checkout by hand can still mean "paid on the last screen",
  // and the webhook often lands a few seconds after that: ask now, and keep
  // asking for a while, so the prep opens by itself rather than being sold
  // a second time.
  watch(checkoutUrl, async (url) => {
    if (url || paid.value || !poll) return
    if (!(await checkPaid())) startPoll()
  })

  onBeforeUnmount(stopPoll)

  return {
    checkoutUrl,
    isStarting: computed(() => isStarting.value || isScriptLoading.value),
    error,
    paid,
    canBuy,
    buy,
  }
}
